<template>
  <div class="groups-page eh-ds">
    <div class="groups-header">
      <div class="groups-header-top">
        <h1>{{ organizationDisplayName }}: Groups</h1>
        <help-icon help-id="groups" title="Help for Groups" />
      </div>

      <div class="groups-search-row">
        <div class="search-field">
          <input
            v-model="groupSearchQuery"
            type="text"
            placeholder="search groups"
            @keyup.enter="applyGroupSearch"
          />
        </div>

        <label class="radio">
          <input type="checkbox" v-model="includeSubgroupsInSearch" />
          <span>include sub-groups</span>
        </label>
      </div>

    </div>

    <div class="groups-body">
      <div class="groups-grid">
        <div class="groups-panel primary-groups-panel">
          <div class="panel-step">1</div>
          <div class="groups-heading">groups</div>
          <p class="panel-help">Choose a top-level group or subgroup.</p>

          <div class="panel-header-row">
            <button class="btn btn-secondary" @click="goUpLevel" :disabled="!canGoUpLevel">
              back up a level
            </button>
          </div>

          <Tree
            v-model:expandedKeys="primaryExpandedKeys"
            v-model:selectionKeys="primarySelectedKeys"
            :value="orgGroupTreeNodes"
            selectionMode="single"
            class="groups-tree"
            @nodeSelect="onPrimaryTreeNodeSelect"
          >
            <template #default="slotProps">
              <div
                class="tree-node-label tree-node-label--group"
                @click.stop="onPrimaryTreeRowClick(slotProps.node)"
              >
                <span class="tree-node-label-text">{{ slotProps.node.label }}</span>
              </div>
            </template>
          </Tree>

          <div class="selected-box">
            <div class="selected-line">
              selected:
              <strong>{{ selectedGroupName || patientDisplayName || "—" }}</strong>
            </div>

            <div class="selected-actions">
              <button
                class="btn btn-outline"
                @click="startRename"
                :disabled="!selectedGroupId || selectedGroupIsSystemDefault"
              >
                rename
              </button>
              <button
                class="btn btn-outline danger"
                @click="deleteSelectedGroup"
                :disabled="!selectedGroupId || selectedGroupIsSystemDefault"
              >
                delete
              </button>
            </div>
          </div>

          <div class="form-inline">
            <button
              class="btn btn-success"
              @click="startCreateSubgroup"
              :disabled="!selectedGroupId && !selectedPatientUniqueId"
            >
              create new subgroup
            </button>
          </div>

          <div v-if="showRenameForm" class="form-inline rename-form">
            <input v-model="renameValue" type="text" placeholder="New name" />
            <div class="form-inline-actions">
              <button class="btn btn-primary" @click="saveRename">save</button>
              <button class="btn btn-secondary" @click="cancelRename">quit</button>
            </div>
          </div>

          <div v-if="showCreateForm" class="form-inline rename-form">
            <input v-model="createValue" type="text" placeholder="Subgroup name" />
            <div class="form-inline-actions">
              <button class="btn btn-primary" @click="saveCreateSubgroup">save</button>
              <button class="btn btn-secondary" @click="cancelCreateSubgroup">quit</button>
            </div>
          </div>
        </div>

        <div class="groups-panel hierarchy-panel">
          <div class="panel-step">2</div>
          <div class="groups-heading">users and subgroups</div>
          <p class="panel-help">
            {{ activePrimaryGroupKey ? "Choose a user or their subgroup." : "Select a group to continue." }}
          </p>

          <Tree
            v-if="activePrimaryGroupKey && secondaryTreeNodes.length"
            v-model:expandedKeys="expandedTreeKeys"
            v-model:selectionKeys="selectedTreeKeys"
            :value="secondaryTreeNodes"
            selectionMode="single"
            class="groups-tree"
            @nodeSelect="onSecondaryTreeNodeSelect"
            @node-expand="onTreeNodeExpand"
          >
            <template #default="slotProps">
              <div
                class="tree-node-label"
                :class="{
                  'tree-node-label--group': isTreeGroupNode(slotProps.node),
                  'tree-node-label--person': isTreePersonNode(slotProps.node),
                }"
                @click.stop="onSecondaryTreeRowClick(slotProps.node)"
              >
                <i v-if="isTreePersonNode(slotProps.node)" class="pi pi-user user-silhouette-icon" aria-hidden="true"></i>
                <span class="tree-node-label-text">{{ slotProps.node.label }}</span>
              </div>
            </template>
          </Tree>
          <div v-else-if="!activePrimaryGroupKey" class="hierarchy-empty">
            <i class="pi pi-arrow-left" aria-hidden="true"></i>
            <span>Start by selecting a group.</span>
          </div>
          <div v-else class="hierarchy-empty">
            <span>This group has no users.</span>
          </div>
        </div>

        <!-- Existing assignment behavior, presented as step 3. -->
        <div class="groups-panel users-panel" ref="usersPanel">
          <div class="panel-step">3</div>
          <div class="users-panel-heading">
            <div>
              <!-- Person click: global subgroup-member list; group click: normal assignment. -->
              <div class="users-heading">{{ isOwnerSubgroupRollup ? "subgroup members" : "assignment" }}</div>
              <p class="panel-help">
                {{ panel3HelpText }}
              </p>
            </div>
            <div class="users-top">
              <input v-model="userSearchQuery" type="text" placeholder="search users" />
            </div>
          </div>

          <div class="assignment-column-headings">
            <div>
              <strong>unassigned {{ unassignedLabelSuffix }}</strong>
            </div>
            <div>
              <strong>assigned users</strong>
            </div>
          </div>

          <div v-if="!hasSecondarySelection" class="assignment-empty">
            <i class="pi pi-arrow-left" aria-hidden="true"></i>
            <span>Select a group to view assignments.</span>
          </div>

          <!-- no-assign hides the right/assign arrow for the person-rollup picklist. -->
          <div
            v-show="hasSecondarySelection"
            class="groups-picklist-wrap"
            :class="{ 'groups-picklist-wrap--no-assign': isOwnerSubgroupRollup }"
            @click.capture="onPickListTransferClick"
          >
            <PickList
              v-model="pickListUsers"
              :selection="pickListSelection"
              @update:selection="onPickListSelectionChange"
              dataKey="unique_ID"
              :showSourceControls="false"
              :showTargetControls="false"
              filter
              filterBy="name,email"
              class="groups-picklist"
              :sourceHeader="'assigned users'"
              :targetHeader="'unassigned users'"
            >
              <template #option="{ option }">
                <div class="pick-item">
                  <div class="pick-name">{{ option.name || option.unique_ID }}</div>
                  <div class="pick-email">{{ option.email || option.username || '' }}</div>
                </div>
              </template>
            </PickList>
          </div>

          <div v-show="hasSecondarySelection" class="picklist-actions">
            <button
              class="btn btn-primary"
              @click="saveAssignChanges"
              :disabled="!canSaveAssignment || assignmentBusy || !assignmentPickListDirty"
            >
              save
            </button>
            <button class="btn btn-secondary" @click="quitAssignChanges" :disabled="!canSaveAssignment || assignmentBusy">
              quit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { CURR_API } from "@/constants/apis";
import HelpIcon from "@/components/help/HelpIcon.vue";
import { useOrganizationStore } from "@/stores/organizationStore";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";

const SYSTEM_DEFAULT_GROUP_NAMES = new Set([
  "administrators",
  "home health aids",
  "nurses",
  "patients",
  "providers",
  "allied professionals",
  // Org-level GCM group is a system default (not a custom subgroup).
  "geriatric care managers",
]);

/**
 * Groups page (3-panel redesign):
 * 1) org + org-subgroup tree (rename/delete/create live here)
 * 2) users of the selected org group + those users' owned subgroups
 * 3) assignment picklist, or (person selected) union of that user's subgroup members
 */
export default {
  name: "GroupsPage",
  components: {
    HelpIcon,
  },
  data() {
    return {
      groupsAll: [],
      groupSearchQuery: "",
      includeSubgroupsInSearch: false,

      selectedGroupId: null,
      // Panel 1 selection key (org / org-subgroup). Drives panel 2 member list + enables panel 3.
      activePrimaryGroupKey: null,
      // True once the user has picked something that should show assignment (panel 3).
      hasSecondarySelection: false,

      // Rename
      showRenameForm: false,
      renameValue: "",

      // Create
      showCreateForm: false,
      createValue: "",

      // Modal data
      accountUsers: [],
      groupMembers: [],
      userSearchQuery: "",
      loadingGroups: false,
      loadingMembers: false,
      loadingUsers: false,
      assignmentBusy: false,
      memberSnapshotIds: [],
      pickListUsers: [[], []],
      pickListSelection: [[], []],
      // Panel 1: org / org-subgroup tree
      primaryExpandedKeys: {},
      primarySelectedKeys: {},
      // Panel 2: users + user-owned subgroups
      expandedTreeKeys: {},
      // PrimeVue 4 Tree single selection expects { [nodeKey]: true }, not a string.
      selectedTreeKeys: {},
      // Members of org group named "Patients" (shown as tree nodes); owner-scoped groups loaded per patient.
      patientsMembers: [],
      loadedGlobalGroupMembers: {},
      loadedOwnerGroups: {},
      selectedPatientUniqueId: null,
      /** Global group id when a person node is selected (Patients vs Nurses/Providers/HHA). */
      selectedPersonGlobalGroupId: null,
      // Maps each rolled-up member to the owner's subgroup ids they sit in (for unassign-all).
      ownerSubgroupMemberships: {},
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),

    accountNumber() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).accountnumber;
    },

    organizationDisplayName() {
      return useOrganizationStore().organizationName || "Organization";
    },

    actorUniqueId() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).unique_ID;
    },

    patientsGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          String(g.name || "")
            .trim()
            .toLowerCase() === "patients" &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },

    nursesGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["nurses", "nurse"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },

    providersGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["providers", "provider"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },

    homeHealthAidsGroupId() {
      const match = (this.groupsAll || []).find((g) => {
        const n = String(g.name || "")
          .trim()
          .toLowerCase();
        return (
          (n === "home health aid" || n === "home health aids" || n === "home health aide" || n === "home health aides") &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
        );
      });
      return match ? Number(match.id) : null;
    },

    alliedProfessionalsGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["allied professionals", "allied professional"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },

    // Org-level "Geriatric Care Managers" group id (no owner), used like Allied/Nurses.
    geriatricCareManagersGroupId() {
      const match = (this.groupsAll || []).find(
        (g) =>
          ["geriatric care managers", "geriatric care manager"].includes(
            String(g.name || "")
              .trim()
              .toLowerCase()
          ) &&
          (g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "")
      );
      return match ? Number(match.id) : null;
    },

    memberInjectableGroupIds() {
      return new Set(
        [
          this.patientsGroupId,
          this.nursesGroupId,
          this.providersGroupId,
          this.homeHealthAidsGroupId,
          this.alliedProfessionalsGroupId,
          this.geriatricCareManagersGroupId,
        ].filter((id) => Number(id) > 0)
      );
    },

    ownerGroupsFlat() {
      return Object.values(this.loadedOwnerGroups || {}).flat();
    },

    groupsById() {
      const map = {};
      for (const g of this.groupsAll || []) {
        map[g.id] = g;
        map[String(g.id)] = g;
      }
      for (const g of this.ownerGroupsFlat) {
        map[g.id] = g;
        map[String(g.id)] = g;
      }
      return map;
    },

    selectedGroup() {
      if (!this.selectedGroupId) return null;
      return this.groupsById[this.selectedGroupId] || this.groupsById[String(this.selectedGroupId)] || null;
    },

    selectedGroupName() {
      return this.selectedGroup ? this.selectedGroup.name : "";
    },

    selectedGroupIsSystemDefault() {
      const n = String(this.selectedGroupName || "").trim().toLowerCase();
      return n !== "" && SYSTEM_DEFAULT_GROUP_NAMES.has(n);
    },

    patientDisplayName() {
      if (!this.selectedPatientUniqueId) return "";
      return this.ownerDisplayName(this.selectedPatientUniqueId);
    },

    subgroupContextTitle() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) return this.patientDisplayName || "selected patient";
      return this.selectedGroupName || "selected group";
    },

    selectedGroupRoleFilter() {
      const name = String(this.selectedGroupName || "")
        .trim()
        .toLowerCase();
      if (["patients", "patient"].includes(name)) return "patient";
      if (["providers", "provider"].includes(name)) return "provider";
      if (["nurses", "nurse"].includes(name)) return "nurse";
      if (["home health aid", "home health aids", "home health aide", "home health aides"].includes(name)) return "hha";
      if (["allied professionals", "allied professional"].includes(name)) return "allied";
      // Role filter key for GCM group selection / member pickers.
      if (["geriatric care managers", "geriatric care manager"].includes(name)) return "gcm";
      return "";
    },

    unassignedLabelSuffix() {
      if (this.selectedGroupRoleFilter === "patient") return "patients";
      if (this.selectedGroupRoleFilter === "provider") return "providers";
      if (this.selectedGroupRoleFilter === "nurse") return "nurses";
      if (this.selectedGroupRoleFilter === "hha") return "home health aids";
      if (this.selectedGroupRoleFilter === "allied") return "allied professionals";
      if (this.selectedGroupRoleFilter === "gcm") return "geriatric care managers";
      return "users";
    },

    /** Person in panel 2 (not a subgroup): panel 3 lists every member of their owned subgroups. */
    isOwnerSubgroupRollup() {
      // selectedGroupId is cleared on person click so this mode is distinct from subgroup assign.
      return !!(this.selectedPatientUniqueId && !this.selectedGroupId);
    },

    // Save/quit stay enabled for a person rollup even though no single group_id is selected.
    canSaveAssignment() {
      return !!(this.selectedGroupId || this.isOwnerSubgroupRollup);
    },

    panel3HelpText() {
      if (!this.hasSecondarySelection) return "Choose a group to manage users.";
      // Spell out that this list is unassign-only, not a place to add members.
      if (this.isOwnerSubgroupRollup) {
        return `${this.currentPathText} — members of this user's subgroups (unassign only).`;
      }
      return this.currentPathText;
    },

    /** True when assigned-member IDs differ from last load (chevron moves). */
    assignmentPickListDirty() {
      // Person rollup uses the same dirty check against memberSnapshotIds.
      if (!this.canSaveAssignment) return false;
      const original = new Set((this.memberSnapshotIds || []).map(String));
      const now = new Set((this.pickListUsers[0] || []).map((u) => String(u.unique_ID)));
      if (original.size !== now.size) return true;
      for (const id of original) {
        if (!now.has(id)) return true;
      }
      return false;
    },

    parentOfSelected() {
      if (!this.selectedGroup) return null;
      if (!this.selectedGroup.parent_group_id) return null;
      const pid = Number(this.selectedGroup.parent_group_id);
      return this.groupsById[pid] || null;
    },

    /** True when "back up a level" can move one step toward the breadcrumb root. */
    canGoUpLevel() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const uid = this.selectedPatientUniqueId;
        const ggid =
          this.selectedPersonGlobalGroupId != null && Number(this.selectedPersonGlobalGroupId) > 0
            ? Number(this.selectedPersonGlobalGroupId)
            : this.globalGroupIdForUid(uid);
        return ggid != null && Number(ggid) > 0;
      }
      if (!this.selectedGroupId) return false;
      const g = this.selectedGroup;
      if (!g) return false;
      const pp = g.parent_group_id;
      const hasParent = pp != null && String(pp).trim() !== "" && Number(pp) !== 0;
      if (hasParent) {
        const pid = Number(pp);
        return !!this.groupsById[pid];
      }
      const owner =
        g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== "" ? String(g.owner_unique_ID).trim() : "";
      if (owner) return true;
      return !!this.parentOfSelected;
    },

    parentId() {
      return this.parentOfSelected ? this.parentOfSelected.id : null;
    },

    ancestorsPath() {
      // Build path from selected group up to root (then reverse for display)
      if (!this.selectedGroupId) return [];
      const path = [];
      let currentId = Number(this.selectedGroupId);
      const visited = new Set();
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const g = this.groupsById[currentId];
        if (!g) break;
        path.push(g);
        if (!g.parent_group_id) break;
        currentId = Number(g.parent_group_id);
      }
      return path.reverse();
    },
    displayedGroupList() {
      return this.ancestorsPath;
    },
    groupTreeNodes() {
      // Full org+member tree (legacy shape). Panel UI now uses orgGroupTreeNodes +
      // secondaryTreeNodes; kept for subtree lookups / fallbacks.
      const byParent = {};
      const nodesById = {};

      const groups = this.groupsAll || [];

      groups.forEach((g) => {
        const gid = Number(g.id);
        const shouldInjectMembers = this.shouldInjectGroupMembers(gid);
        const node = {
          key: String(g.id),
          label: g.name,
          data: { type: "group", group: g },
          // Force a caret for role globals and custom subgroups so members can appear on expand.
          leaf: shouldInjectMembers ? false : undefined,
          children: [],
        };
        nodesById[String(g.id)] = node;
        const parentKey =
          g.parent_group_id === null || g.parent_group_id === "" || Number(g.parent_group_id) === 0
            ? "__root__"
            : String(g.parent_group_id);
        if (!byParent[parentKey]) byParent[parentKey] = [];
        byParent[parentKey].push(node);
      });

      Object.keys(nodesById).forEach((id) => {
        const children = byParent[id] || [];
        children.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        nodesById[id].children = children;
      });

      const roots = (byParent["__root__"] || []).sort((a, b) => {
        const aIsPatients = String(a.label || "").trim().toLowerCase() === "patients";
        const bIsPatients = String(b.label || "").trim().toLowerCase() === "patients";
        if (aIsPatients !== bIsPatients) return aIsPatients ? -1 : 1;
        return (a.label || "").localeCompare(b.label || "");
      });

      const injectMembers = (nodes) => {
        for (const n of nodes) {
          const groupId = Number(n.key);
          if (this.shouldInjectGroupMembers(groupId)) {
            const orgKids = [...(n.children || [])];
            const memberRows =
              groupId === this.patientsGroupId
                ? this.patientsMembers || []
                : this.loadedGlobalGroupMembers[String(groupId)] || [];
            const sortedMemberRows = [...memberRows].sort((a, b) => this.compareUsersByLastFirst(a, b));
            const memberKids = sortedMemberRows.map((m) => {
              const uid = String(m.unique_ID);
              if (groupId !== this.patientsGroupId) {
                return {
                  key: `member:${groupId}:${uid}`,
                  label: this.formatMemberName(m) || uid,
                  data: { type: "member", unique_ID: uid, member: m, group_id: groupId },
                  leaf: false,
                  children: this.buildOwnerGroupTreeNodes(this.loadedOwnerGroups[uid] || []),
                };
              }
              return {
                key: `patient:${uid}`,
                label: this.formatMemberName(m) || uid,
                data: { type: "patient", unique_ID: uid, member: m },
                leaf: false,
                children: this.buildOwnerGroupTreeNodes(this.loadedOwnerGroups[uid] || []),
              };
            });
            // Keep group rows alphabetic, and keep person rows in strict last/first order.
            n.children = [...orgKids, ...memberKids];
            // Nested custom subgroups under this group also need member injection.
            injectMembers(orgKids);
          } else if (n.children && n.children.length) {
            injectMembers(n.children);
          }
        }
      };
      injectMembers(roots);
      return roots;
    },

    /** Panel 1: top-level orgs and their org subgroups only (no people). */
    orgGroupTreeNodes() {
      const byParent = {};
      const nodesById = {};
      // Owner-scoped groups belong under people in panel 2, not in the org tree.
      const groups = (this.groupsAll || []).filter((g) => this.isOrgCatalogGroup(g));

      groups.forEach((g) => {
        const node = {
          key: String(g.id),
          label: g.name,
          data: { type: "group", group: g },
          children: [],
        };
        nodesById[String(g.id)] = node;
        const parentKey =
          g.parent_group_id === null || g.parent_group_id === "" || Number(g.parent_group_id) === 0
            ? "__root__"
            : String(g.parent_group_id);
        if (!byParent[parentKey]) byParent[parentKey] = [];
        byParent[parentKey].push(node);
      });

      Object.keys(nodesById).forEach((id) => {
        const children = byParent[id] || [];
        children.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        nodesById[id].children = children;
        nodesById[id].leaf = children.length === 0;
      });

      // Parents that aren't org catalog groups (e.g. missing / owner-scoped): treat kids as roots.
      const roots = [...(byParent["__root__"] || [])];
      Object.keys(byParent).forEach((k) => {
        if (k === "__root__" || nodesById[k]) return;
        for (const n of byParent[k] || []) {
          if (!roots.includes(n)) roots.push(n);
        }
      });

      return roots.sort((a, b) => {
        // Product rule: Patients is always the first top-level org in panel 1.
        const aIsPatients = String(a.label || "").trim().toLowerCase() === "patients";
        const bIsPatients = String(b.label || "").trim().toLowerCase() === "patients";
        if (aIsPatients !== bIsPatients) return aIsPatients ? -1 : 1;
        return (a.label || "").localeCompare(b.label || "");
      });
    },

    /**
     * Panel 2: members of the panel-1 selection, each with their owned subgroups nested.
     * Org subgroups stay in panel 1 — only people + user-owned groups appear here.
     */
    secondaryTreeNodes() {
      if (!this.activePrimaryGroupKey) return [];
      const groupId = Number(this.activePrimaryGroupKey);
      if (!Number.isFinite(groupId) || groupId <= 0) return [];

      // Same membership cache as the old injected tree; panel 3 uses groupMembers separately.
      const memberRows =
        groupId === this.patientsGroupId
          ? this.patientsMembers || []
          : this.loadedGlobalGroupMembers[String(groupId)] || [];

      return [...memberRows]
        .sort((a, b) => this.compareUsersByLastFirst(a, b))
        .map((m) => {
          const uid = String(m.unique_ID);
          if (groupId === this.patientsGroupId) {
            return {
              key: `patient:${uid}`,
              label: this.formatMemberName(m) || uid,
              data: { type: "patient", unique_ID: uid, member: m },
              leaf: false,
              children: this.buildOwnerGroupTreeNodes(this.loadedOwnerGroups[uid] || []),
            };
          }
          return {
            key: `member:${groupId}:${uid}`,
            label: this.formatMemberName(m) || uid,
            data: { type: "member", unique_ID: uid, member: m, group_id: groupId },
            leaf: false,
            children: this.buildOwnerGroupTreeNodes(this.loadedOwnerGroups[uid] || []),
          };
        });
    },

    directChildren() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const owned = this.loadedOwnerGroups[this.selectedPatientUniqueId] || [];
        return owned
          .filter((g) => g.parent_group_id === null || g.parent_group_id === "" || Number(g.parent_group_id) === 0)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      if (!this.selectedGroupId) return [];
      const pid = Number(this.selectedGroupId);
      const catalog = [...(this.groupsAll || []), ...this.ownerGroupsFlat];
      const children = catalog
        .filter((g) => Number(g.parent_group_id || 0) === pid)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      return children;
    },

    currentPathText() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const root =
          this.selectedPersonGlobalGroupId != null && Number(this.selectedPersonGlobalGroupId) > 0
            ? this.labelForGlobalGroupId(this.selectedPersonGlobalGroupId)
            : this.ownerRootGroupLabel(this.selectedPatientUniqueId);
        return `${root} > ${this.patientDisplayName || "Unknown user"}`;
      }
      if (!this.selectedGroupId) return "—";
      const g = this.selectedGroup;
      if (!g) return "—";
      const o = g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== "" ? String(g.owner_unique_ID).trim() : "";
      if (o) {
        const parts = [];
        let cur = g;
        const seen = new Set();
        while (cur && !seen.has(cur.id)) {
          seen.add(cur.id);
          parts.unshift(cur.name || "");
          const pp = cur.parent_group_id;
          if (pp === null || pp === "" || Number(pp) === 0) break;
          cur = this.groupsById[Number(pp)];
        }
        const ownerRoot = this.ownerRootGroupLabel(o);
        const ownerName = this.ownerDisplayName(o);
        return `${ownerRoot} > ${ownerName} > ${parts.filter(Boolean).join(" > ")}`;
      }
      if (!this.ancestorsPath.length) return "—";
      const names = this.ancestorsPath.map((gr) => gr.name).filter(Boolean);
      return names.join(" > ");
    },

  },
  watch: {
    groupSearchQuery() {
      this.applyGroupSearch();
    },
    includeSubgroupsInSearch() {
      this.applyGroupSearch();
    },
    selectedGroupId: {
      immediate: false,
      async handler(newId) {
        this.syncTreeStateFromSelection();
        if (this.selectedGroupIsSystemDefault && this.showRenameForm) {
          this.cancelRename();
        }
        if (!newId) return;
        await this.refreshUsersPanel();
      },
    },
    activePrimaryGroupKey: {
      // Lazy-load panel 2 members when the panel-1 selection changes.
      async handler(newKey, oldKey) {
        if (!newKey || String(newKey) === String(oldKey)) return;
        const gid = Number(newKey);
        if (!Number.isFinite(gid) || gid <= 0) return;
        if (gid === this.patientsGroupId) {
          if (!(this.patientsMembers || []).length) await this.loadPatientsMembersForTree();
          return;
        }
        const loaded = Object.prototype.hasOwnProperty.call(this.loadedGlobalGroupMembers, String(gid));
        if (!loaded) await this.loadGlobalGroupMembersForTree(gid);
      },
    },
    userSearchQuery() {
      this.rebuildPickList();
    },
    groupsAll: {
      deep: true,
      handler() {
        this.syncTreeStateFromSelection();
      },
    },
  },
  mounted() {
    if (this.accountNumber) {
      useOrganizationStore().ensureLoaded(this.accountNumber);
    }
    this.loadGroups();
  },
  methods: {
    // Org catalog = no owner_unique_ID (account-level groups / subgroups for panel 1).
    isOrgCatalogGroup(g) {
      if (!g) return false;
      return g.owner_unique_ID == null || String(g.owner_unique_ID).trim() === "";
    },

    // Panel 1 click: select org/subgroup, clear panel-2 person selection, open assignment.
    async selectPrimaryGroup(node) {
      if (!node?.key) return;
      const keyStr = String(node.key);
      this.activePrimaryGroupKey = keyStr;
      this.hasSecondarySelection = true;
      this.primarySelectedKeys = { [keyStr]: true };
      this.selectedTreeKeys = {};
      this.expandedTreeKeys = {};
      this.selectedPatientUniqueId = null;
      this.selectedPersonGlobalGroupId = null;

      const id = Number(keyStr);
      if (!Number.isFinite(id) || id <= 0) return;
      this.selectedGroupId = id;
      this.showRenameForm = false;
      this.showCreateForm = false;
      // Panel 2 members: activePrimaryGroupKey watcher.
      // Panel 3 picklist: selectedGroupId watcher.
    },

    async onPrimaryTreeNodeSelect(node) {
      await this.selectPrimaryGroup(node);
    },

    // Row click toggles expand/collapse independently of selection.
    async onPrimaryTreeRowClick(node) {
      const key = node?.key != null ? String(node.key) : "";
      if (!key) return;
      const shouldExpand = !this.primaryExpandedKeys?.[key];
      await this.selectPrimaryGroup(node);
      await this.$nextTick();
      const expanded = { ...(this.primaryExpandedKeys || {}) };
      if (shouldExpand) {
        expanded[key] = true;
      } else {
        delete expanded[key];
      }
      this.primaryExpandedKeys = expanded;
    },

    // Panel 2 selection (person or user-owned subgroup) — same activate path as before.
    async onSecondaryTreeNodeSelect(node) {
      this.hasSecondarySelection = true;
      await this.onTreeNodeSelect(node);
    },

    async onSecondaryTreeRowClick(node) {
      this.hasSecondarySelection = true;
      await this.onTreeRowClick(node);
    },

    currentSelectedTreeKey() {
      const keys = this.selectedTreeKeys || {};
      const selected = Object.keys(keys).find((k) => !!keys[k]);
      return selected || null;
    },

    findTreeNodeByKey(nodes, targetKey) {
      const list = Array.isArray(nodes) ? nodes : [];
      for (const n of list) {
        if (String(n.key) === String(targetKey)) return n;
        const hit = this.findTreeNodeByKey(n.children || [], targetKey);
        if (hit) return hit;
      }
      return null;
    },

    collectSubtreeKeys(node, out = new Set()) {
      if (!node) return out;
      out.add(String(node.key));
      const children = Array.isArray(node.children) ? node.children : [];
      for (const c of children) this.collectSubtreeKeys(c, out);
      return out;
    },

    subtreeKeysForKey(rootKey) {
      if (!rootKey) return new Set();
      const node =
        this.findTreeNodeByKey(this.secondaryTreeNodes || [], rootKey) ||
        this.findTreeNodeByKey(this.orgGroupTreeNodes || [], rootKey) ||
        this.findTreeNodeByKey(this.groupTreeNodes || [], rootKey);
      if (!node) return new Set([String(rootKey)]);
      return this.collectSubtreeKeys(node, new Set());
    },

    collapseDeselectedBranch(prevKey, nextKey) {
      if (!prevKey || !nextKey || String(prevKey) === String(nextKey)) return;
      const prevSubtree = this.subtreeKeysForKey(prevKey);
      const nextSubtree = this.subtreeKeysForKey(nextKey);
      const sameBranch = prevSubtree.has(String(nextKey)) || nextSubtree.has(String(prevKey));
      if (sameBranch) return;

      const nextExpanded = { ...(this.expandedTreeKeys || {}) };
      for (const k of prevSubtree) {
        delete nextExpanded[String(k)];
      }
      this.expandedTreeKeys = nextExpanded;
    },

    normalizeApiPayload(payload) {
      try {
        if (typeof payload === "string") return JSON.parse(payload);
      } catch (e) {
        // ignore
      }
      return payload;
    },

    normalizeAccountType(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },

    matchesRoleFilter(user) {
      const filter = this.selectedGroupRoleFilter;
      if (!filter) return true;
      const t = this.normalizeAccountType(user?.account_type);
      if (filter === "patient") return t === "patient";
      if (filter === "provider") return t === "provider";
      if (filter === "nurse") return ["nurse", "registered nurse", "licensed practical nurse"].includes(t);
      if (filter === "hha") return ["home health aid", "home health aids", "home health aide", "home health aides"].includes(t);
      if (filter === "allied") return ["allied professional", "allied professionals"].includes(t);
      // Keep assignable pick-list users limited to GCM account types when that group is selected.
      if (filter === "gcm") return ["geriatric care manager", "geriatric care managers"].includes(t);
      return true;
    },

    isActiveAccountUser(user) {
      return String(user?.account_status || "").trim().toLowerCase() === "active";
    },

    patientLabelForOwner(uid) {
      return this.ownerDisplayName(uid);
    },

    ownerDisplayName(uid) {
      const id = String(uid || "").trim();
      if (!id) return "Unknown user";

      const fromPatients = (this.patientsMembers || []).find((x) => String(x.unique_ID) === id);
      if (fromPatients) return this.formatMemberName(fromPatients);

      for (const gid of Object.keys(this.loadedGlobalGroupMembers || {})) {
        const rows = this.loadedGlobalGroupMembers[gid] || [];
        const hit = rows.find((x) => String(x.unique_ID) === id);
        if (hit) return this.formatMemberName(hit);
      }

      const au = (this.accountUsers || []).find((u) => String(u.unique_ID) === id);
      if (au) return au.name || au.username || au.email || "Unknown user";

      return "Unknown user";
    },

    labelForGlobalGroupId(gid) {
      const n = Number(gid);
      if (this.patientsGroupId && n === Number(this.patientsGroupId)) return "Patients";
      if (this.nursesGroupId && n === Number(this.nursesGroupId)) return "Nurses";
      if (this.providersGroupId && n === Number(this.providersGroupId)) return "Providers";
      if (this.homeHealthAidsGroupId && n === Number(this.homeHealthAidsGroupId)) return "Home Health Aids";
      if (this.alliedProfessionalsGroupId && n === Number(this.alliedProfessionalsGroupId)) return "Allied Professionals";
      // Display label for the org-level GCM global group in the tree.
      if (this.geriatricCareManagersGroupId && n === Number(this.geriatricCareManagersGroupId)) {
        return "Geriatric Care Managers";
      }
      return "Users";
    },

    ownerRootGroupLabel(uid) {
      const id = String(uid || "").trim();
      if (!id) return "Users";

      const t = this.normalizeAccountType(
        ((this.accountUsers || []).find((u) => String(u.unique_ID) === id) || {}).account_type
      );
      if (t === "patient") return "Patients";
      if (t === "provider") return "Providers";
      if (["nurse", "registered nurse", "licensed practical nurse"].includes(t)) return "Nurses";
      if (["home health aid", "home health aids", "home health aide", "home health aides"].includes(t)) {
        return "Home Health Aids";
      }
      if (["allied professional", "allied professionals"].includes(t)) return "Allied Professionals";
      // Infer root label from account_type when the user is a GCM.
      if (["geriatric care manager", "geriatric care managers"].includes(t)) {
        return "Geriatric Care Managers";
      }

      if ((this.patientsMembers || []).some((x) => String(x.unique_ID) === id)) return "Patients";

      const rolePairs = [
        [this.nursesGroupId, "Nurses"],
        [this.providersGroupId, "Providers"],
        [this.homeHealthAidsGroupId, "Home Health Aids"],
        [this.alliedProfessionalsGroupId, "Allied Professionals"],
        [this.geriatricCareManagersGroupId, "Geriatric Care Managers"],
      ];
      for (const [gid, label] of rolePairs) {
        if (!gid) continue;
        const rows = (this.loadedGlobalGroupMembers || {})[String(gid)] || [];
        if (rows.some((x) => String(x.unique_ID) === id)) return label;
      }
      return "Users";
    },

    isTreePersonNode(node) {
      return node?.data?.type === "patient" || node?.data?.type === "member";
    },

    isTreeGroupNode(node) {
      return !this.isTreePersonNode(node);
    },

    /** Role globals + any group with a parent (custom subgroups) get members as tree children. */
    shouldInjectGroupMembers(groupId) {
      const gid = Number(groupId);
      if (!Number.isFinite(gid) || gid <= 0) return false;
      if (this.memberInjectableGroupIds.has(gid)) return true;
      const g = this.groupsById[gid] || this.groupsById[String(gid)];
      if (!g) return false;
      const parent = g.parent_group_id;
      return parent != null && String(parent).trim() !== "" && Number(parent) !== 0;
    },

    buildOwnerGroupTreeNodes(ownedList) {
      const list = Array.isArray(ownedList) ? ownedList : [];
      if (!list.length) return [];
      const byParent = {};
      const nodesById = {};
      list.forEach((g) => {
        const node = {
          key: String(g.id),
          label: g.name,
          data: { type: "group", group: g },
          children: [],
        };
        nodesById[String(g.id)] = node;
        const parentKey =
          g.parent_group_id === null || g.parent_group_id === "" || Number(g.parent_group_id) === 0
            ? "__root__"
            : String(g.parent_group_id);
        if (!byParent[parentKey]) byParent[parentKey] = [];
        byParent[parentKey].push(node);
      });
      Object.keys(nodesById).forEach((id) => {
        const children = byParent[id] || [];
        children.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        nodesById[id].children = children;
      });
      return (byParent["__root__"] || []).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    },

    async loadPatientsMembersForTree() {
      this.patientsMembers = [];
      if (!this.accountNumber || !this.patientsGroupId) return;
      try {
        const content = {
          Groups: {
            API_Meth: "GetGroupMembers",
            accountnumber: this.accountNumber,
            group_id: this.patientsGroupId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.patientsMembers = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error("Failed to load Patients group members:", e);
        this.patientsMembers = [];
      }
    },

    async loadGlobalGroupMembersForTree(groupId) {
      const gid = Number(groupId);
      if (!this.accountNumber || !gid) return;
      try {
        const content = {
          Groups: {
            API_Meth: "GetGroupMembers",
            accountnumber: this.accountNumber,
            group_id: gid,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.loadedGlobalGroupMembers = {
          ...this.loadedGlobalGroupMembers,
          [String(gid)]: Array.isArray(list) ? list : [],
        };
      } catch (e) {
        console.error("Failed to load global group members:", e);
        this.loadedGlobalGroupMembers = {
          ...this.loadedGlobalGroupMembers,
          [String(gid)]: [],
        };
      }
    },

    async fetchOwnerGroups(ownerUniqueId) {
      const uid = ownerUniqueId != null ? String(ownerUniqueId).trim() : "";
      if (!this.accountNumber || !uid) return;
      try {
        const content = {
          Groups: {
            API_Meth: "GetGroupsByOwner",
            accountnumber: this.accountNumber,
            owner_unique_ID: uid,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        const rows = Array.isArray(list) ? list : [];
        this.loadedOwnerGroups = { ...this.loadedOwnerGroups, [uid]: rows };
      } catch (e) {
        console.error("Failed to load owner groups:", e);
        this.loadedOwnerGroups = { ...this.loadedOwnerGroups, [uid]: [] };
      }
    },

    async loadGroups() {
      if (!this.accountNumber) return;
      this.loadingGroups = true;
      const savedGroupId = this.selectedGroupId;
      const savedGroupRow = this.selectedGroup;
      const savedOwner =
        savedGroupRow && savedGroupRow.owner_unique_ID != null && String(savedGroupRow.owner_unique_ID).trim() !== ""
          ? String(savedGroupRow.owner_unique_ID).trim()
          : null;
      const savedPatientOnly = this.selectedPatientUniqueId && !this.selectedGroupId ? this.selectedPatientUniqueId : null;

      try {
        const content = {
          Groups: {
            API_Meth: "GetGroupsByAccount",
            accountnumber: this.accountNumber,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        this.groupsAll = Array.isArray(list) ? list : [];
        // Membership caches are rebuilt below; wiping avoids stale members after assign/save.
        this.loadedGlobalGroupMembers = {};
        this.loadedOwnerGroups = {};
        await this.loadPatientsMembersForTree();
        const roleGlobalIds = [
          this.nursesGroupId,
          this.providersGroupId,
          this.homeHealthAidsGroupId,
          this.alliedProfessionalsGroupId,
          // Prefetch GCM members so panel 2 can expand GCM owners immediately.
          this.geriatricCareManagersGroupId,
        ].filter((id) => id != null && Number(id) > 0);
        await Promise.all(roleGlobalIds.map((gid) => this.loadGlobalGroupMembersForTree(gid)));

        // Role globals alone are not enough for panel 2 (e.g. Administrators / custom subgroups).
        // Reload the current selection so save-assign does not leave panel 2 empty until re-click.
        const reloadForPanel2 = new Set(
          [savedGroupId, Number(this.activePrimaryGroupKey)]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0 && id !== this.patientsGroupId)
        );
        await Promise.all(
          Array.from(reloadForPanel2)
            .filter((gid) => !roleGlobalIds.some((rid) => Number(rid) === gid))
            .map((gid) => this.loadGlobalGroupMembersForTree(gid))
        );

        if (savedOwner && savedGroupId) {
          await this.fetchOwnerGroups(savedOwner);
        }
        if (savedPatientOnly) {
          // Restore person selection as the subgroup-member rollup, not a single group picklist.
          await this.showOwnerSubgroupRollup(savedPatientOnly);
        } else if (savedGroupId && this.groupsById[savedGroupId]) {
          this.selectedGroupId = Number(savedGroupId);
          this.selectedPatientUniqueId = null;
          this.selectedPersonGlobalGroupId = null;
          this.syncTreeStateFromSelection();
          await this.refreshUsersPanel();
        }
      } catch (e) {
        console.error("Failed to load groups:", e);
      } finally {
        this.loadingGroups = false;
      }
    },

    async applyGroupSearch() {
      const q = (this.groupSearchQuery || "").trim().toLowerCase();
      if (!q || this.groupsAll.length === 0) return;

      let scope = [...this.groupsAll, ...this.ownerGroupsFlat];
      if (!this.includeSubgroupsInSearch && this.selectedGroupId) {
        // When not including sub-groups: limit the scope to ancestors + their direct children.
        const scopeIds = new Set(this.ancestorsPath.map((g) => Number(g.id)));
        const catalog = [...this.groupsAll, ...this.ownerGroupsFlat];
        for (const a of this.ancestorsPath) {
          for (const c of catalog) {
            if (Number(c.parent_group_id || 0) === Number(a.id)) scopeIds.add(Number(c.id));
          }
        }
        scope = catalog.filter((g) => scopeIds.has(Number(g.id)));
      }

      const matches = scope
        .filter((g) => (g.name || "").toLowerCase().includes(q))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      if (matches.length) {
        const m = matches[0];
        this.selectedPatientUniqueId = null;
        this.selectedPersonGlobalGroupId = null;
        this.selectedGroupId = Number(m.id);
        const ow =
          m.owner_unique_ID != null && String(m.owner_unique_ID).trim() !== ""
            ? String(m.owner_unique_ID).trim()
            : "";
        if (ow) await this.fetchOwnerGroups(ow);
        if (ow) {
          this.activePrimaryGroupKey = String(this.globalGroupIdForUid(ow) || "");
        } else {
          // Nested org subgroups are first-class panel-1 nodes (do not walk up to the root).
          this.activePrimaryGroupKey = String(m.id);
        }
        this.hasSecondarySelection = true;
        this.syncTreeStateFromSelection();
      }
    },

    selectGroup(groupId) {
      if (!groupId) return;
      this.selectedPatientUniqueId = null;
      this.selectedPersonGlobalGroupId = null;
      this.selectedGroupId = Number(groupId);
      this.syncTreeStateFromSelection();
    },

    globalGroupIdForUid(uid) {
      const id = String(uid || "").trim();
      if (!id) return null;
      if (this.patientsGroupId && (this.patientsMembers || []).some((x) => String(x.unique_ID) === id)) {
        return this.patientsGroupId;
      }
      for (const gid of [
        this.nursesGroupId,
        this.providersGroupId,
        this.homeHealthAidsGroupId,
        this.alliedProfessionalsGroupId,
        this.geriatricCareManagersGroupId,
      ]) {
        if (!gid) continue;
        const rows = (this.loadedGlobalGroupMembers || {})[String(gid)] || [];
        if (rows.some((x) => String(x.unique_ID) === id)) return gid;
      }
      const au = (this.accountUsers || []).find((u) => String(u.unique_ID) === id);
      if (au) {
        const t = this.normalizeAccountType(au.account_type);
        if (t === "patient") return this.patientsGroupId;
        if (t === "provider") return this.providersGroupId;
        if (["nurse", "registered nurse", "licensed practical nurse"].includes(t)) return this.nursesGroupId;
        if (["home health aid", "home health aids", "home health aide", "home health aides"].includes(t)) {
          return this.homeHealthAidsGroupId;
        }
        if (["allied professional", "allied professionals"].includes(t)) {
          return this.alliedProfessionalsGroupId;
        }
        // Resolve GCM users back to the org-level GCM group for tree expansion.
        if (["geriatric care manager", "geriatric care managers"].includes(t)) {
          return this.geriatricCareManagersGroupId;
        }
      }
      return null;
    },

    /**
     * Expand the person row in panel 2 for an owner-scoped subgroup.
     * Prefer activePrimaryGroupKey so custom org subgroups (not just role globals) stay in sync.
     */
    applyOwnerPersonExpansion(expanded, ownerUid) {
      const o = String(ownerUid || "").trim();
      if (!o) return;

      // Keys in panel 2 are patient:<uid> or member:<orgGroupId>:<uid>.
      const primaryGid = Number(this.activePrimaryGroupKey);
      if (primaryGid && Number.isFinite(primaryGid) && primaryGid > 0) {
        if (this.patientsGroupId != null && primaryGid === Number(this.patientsGroupId)) {
          expanded[`patient:${o}`] = true;
        } else {
          expanded[`member:${primaryGid}:${o}`] = true;
        }
        return;
      }

      let ggid = this.globalGroupIdForUid(o);
      if (ggid == null) {
        const label = this.ownerRootGroupLabel(o);
        if (label === "Patients") ggid = this.patientsGroupId;
        else if (label === "Nurses") ggid = this.nursesGroupId;
        else if (label === "Providers") ggid = this.providersGroupId;
        else if (label === "Home Health Aids") ggid = this.homeHealthAidsGroupId;
        else if (label === "Allied Professionals") ggid = this.alliedProfessionalsGroupId;
        // Fall back to GCM org group when expanding a GCM owner's subgroups.
        else if (label === "Geriatric Care Managers") ggid = this.geriatricCareManagersGroupId;
      }
      if (ggid == null || !Number(ggid)) return;

      const isPatientBranch =
        this.patientsGroupId != null && Number(ggid) === Number(this.patientsGroupId);

      if (isPatientBranch) {
        expanded[`patient:${o}`] = true;
      } else {
        expanded[`member:${ggid}:${o}`] = true;
      }
    },

    selectPatient(uniqueId, globalParentGroupId) {
      const uid = uniqueId != null ? String(uniqueId).trim() : "";
      if (!uid) return;
      this.selectedPatientUniqueId = uid;
      this.selectedGroupId = null;
      if (globalParentGroupId !== undefined && globalParentGroupId !== null && globalParentGroupId !== "") {
        this.selectedPersonGlobalGroupId = Number(globalParentGroupId);
      } else {
        this.selectedPersonGlobalGroupId = this.globalGroupIdForUid(uid);
      }
      this.groupMembers = [];
      this.memberSnapshotIds = [];
      this.pickListUsers = [[], []];
      // Drop stale unique_ID -> group_id[] mapping until the rollup reloads.
      this.ownerSubgroupMemberships = {};
      this.syncTreeStateFromSelection();
    },

    // Panel 3 rollup: select the person, then union members of every subgroup they own.
    async showOwnerSubgroupRollup(uid, globalParentGroupId) {
      this.selectPatient(uid, globalParentGroupId);
      // Need owned subgroup ids before we can GetGroupMembers on each.
      await this.fetchOwnerGroups(uid);
      await this.loadOwnerSubgroupMemberRollup();
    },

    async activateTreeNode(node, ensureExpanded = false) {
      const nodeKey = node?.key;
      if (nodeKey === undefined || nodeKey === null || nodeKey === "") return;
      const keyStr = String(nodeKey);
      const prevSelectedKey = this.currentSelectedTreeKey();
      this.collapseDeselectedBranch(prevSelectedKey, keyStr);

      if (ensureExpanded) {
        this.expandedTreeKeys = {
          ...(this.expandedTreeKeys || {}),
          [keyStr]: true,
        };
      }

      if (keyStr.startsWith("patient:")) {
        // Patients tree person node → global list of everyone in that patient's subgroups.
        const uid = keyStr.slice("patient:".length);
        await this.showOwnerSubgroupRollup(uid, this.patientsGroupId);
        return;
      }

      if (keyStr.startsWith("member:")) {
        const parts = keyStr.split(":");
        const gid = Number(parts[1] || 0);
        const uid = parts[2] || "";
        if (uid) {
          // Nurses/Providers/etc. person node uses the same rollup as a patient click.
          await this.showOwnerSubgroupRollup(uid, gid);
        }
        if (gid && this.shouldInjectGroupMembers(gid) && gid !== this.patientsGroupId) {
          const loaded = Object.prototype.hasOwnProperty.call(this.loadedGlobalGroupMembers, String(gid));
          if (!loaded) await this.loadGlobalGroupMembersForTree(gid);
        }
        return;
      }

      const id = Number(keyStr);
      if (!Number.isFinite(id) || id <= 0) return;
      this.selectGroup(id);
      const g = this.groupsById[id];
      const owner =
        g && g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== ""
          ? String(g.owner_unique_ID).trim()
          : "";
      if (owner) {
        await this.fetchOwnerGroups(owner);
      }
      if (this.shouldInjectGroupMembers(id) && id !== this.patientsGroupId) {
        const loaded = Object.prototype.hasOwnProperty.call(this.loadedGlobalGroupMembers, String(id));
        if (!loaded) await this.loadGlobalGroupMembersForTree(id);
      }
    },

    // PrimeVue Tree emits node-select with the TreeNode as the first argument.
    async onTreeNodeSelect(node) {
      // Do not force-expand here; row click owns open/close toggling.
      await this.activateTreeNode(node, false);
    },

    async onTreeRowClick(node) {
      const key = node?.key != null ? String(node.key) : "";
      if (!key) return;
      const shouldExpand = !this.expandedTreeKeys?.[key];
      await this.activateTreeNode(node, false);
      // Win over selection sync / deferred nodeSelect handlers that may re-open the node.
      await this.$nextTick();
      const expanded = { ...(this.expandedTreeKeys || {}) };
      if (shouldExpand) {
        expanded[key] = true;
      } else {
        delete expanded[key];
      }
      this.expandedTreeKeys = expanded;
    },

    async onTreeNodeExpand(node) {
      // Node is already expanded by PrimeVue; load data without forcing expand again.
      await this.activateTreeNode(node, false);

      const group = node?.data?.type === "group" ? node.data.group : null;
      const groupId = group ? Number(group.id) : null;
      if (groupId && this.shouldInjectGroupMembers(groupId) && groupId !== this.patientsGroupId) {
        const key = String(groupId);
        if (!Object.prototype.hasOwnProperty.call(this.loadedGlobalGroupMembers, key)) {
          await this.loadGlobalGroupMembersForTree(groupId);
        }
      }
      if (node?.data?.type === "member" && node.data.unique_ID) {
        const uid = String(node.data.unique_ID);
        if (Object.prototype.hasOwnProperty.call(this.loadedOwnerGroups, uid)) return;
        await this.fetchOwnerGroups(uid);
      }
      if (node?.data?.type === "patient" && node.data.unique_ID) {
        const uid = String(node.data.unique_ID);
        if (Object.prototype.hasOwnProperty.call(this.loadedOwnerGroups, uid)) return;
        await this.fetchOwnerGroups(uid);
      }
    },

    syncTreeStateFromSelection() {
      // Keep panel 1 (org tree) and panel 2 (people tree) selection keys in sync with
      // selectedGroupId / selectedPatientUniqueId after search, go-up, create, delete, etc.
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        const uid = this.selectedPatientUniqueId;
        const ggid =
          this.selectedPersonGlobalGroupId != null && Number(this.selectedPersonGlobalGroupId) > 0
            ? Number(this.selectedPersonGlobalGroupId)
            : Number(this.activePrimaryGroupKey) || this.globalGroupIdForUid(uid);
        const expanded = { ...(this.expandedTreeKeys || {}) };
        const isPatientBranch =
          this.patientsGroupId != null && Number(ggid) === Number(this.patientsGroupId);

        if (isPatientBranch) {
          this.selectedTreeKeys = { [`patient:${uid}`]: true };
        } else if (ggid) {
          this.selectedTreeKeys = { [`member:${ggid}:${uid}`]: true };
        } else {
          this.selectedTreeKeys = { [`patient:${uid}`]: true };
        }
        // Keep secondary person rows collapsed by default; only preserve existing expands.
        this.expandedTreeKeys = expanded;
        if (ggid) {
          this.activePrimaryGroupKey = String(ggid);
          this.primarySelectedKeys = { [String(ggid)]: true };
        }
        return;
      }
      if (!this.selectedGroupId) {
        this.selectedTreeKeys = {};
        return;
      }
      const selectedId = Number(this.selectedGroupId);
      const selectedGroup = this.groupsById[selectedId];
      const owner =
        selectedGroup &&
        selectedGroup.owner_unique_ID != null &&
        String(selectedGroup.owner_unique_ID).trim() !== ""
          ? String(selectedGroup.owner_unique_ID).trim()
          : "";

      if (owner) {
        // User-owned group → highlight in panel 2 under the person; keep their org open in panel 1.
        let roleGid = Number(this.activePrimaryGroupKey);
        const primaryMembers =
          roleGid === this.patientsGroupId
            ? this.patientsMembers || []
            : this.loadedGlobalGroupMembers[String(roleGid)] || [];
        const inPrimary = primaryMembers.some((m) => String(m.unique_ID) === owner);
        if (!inPrimary || !roleGid) {
          roleGid = this.globalGroupIdForUid(owner);
        }
        if (roleGid) {
          this.activePrimaryGroupKey = String(roleGid);
          this.primarySelectedKeys = { [String(roleGid)]: true };
          const primaryExpanded = { ...(this.primaryExpandedKeys || {}) };
          primaryExpanded[String(roleGid)] = true;
          let walkId = Number(
            (this.groupsById[roleGid] && this.groupsById[roleGid].parent_group_id) || 0
          );
          const seen = new Set();
          while (walkId && !seen.has(walkId)) {
            seen.add(walkId);
            primaryExpanded[String(walkId)] = true;
            const pg = this.groupsById[walkId];
            if (!pg) break;
            walkId = Number(pg.parent_group_id || 0);
          }
          this.primaryExpandedKeys = primaryExpanded;
        }

        // Owner-scoped group lives under a person in panel 2.
        this.selectedTreeKeys = { [String(selectedId)]: true };
        const expanded = { ...(this.expandedTreeKeys || {}) };
        let currentId = selectedGroup ? Number(selectedGroup.parent_group_id || 0) : 0;
        const visited = new Set();
        while (currentId && !visited.has(currentId)) {
          visited.add(currentId);
          expanded[String(currentId)] = true;
          const group = this.groupsById[currentId];
          if (!group) break;
          const parent = Number(group.parent_group_id || 0);
          if (!parent) break;
          currentId = parent;
        }
        this.applyOwnerPersonExpansion(expanded, owner);
        this.expandedTreeKeys = expanded;
        return;
      }

      // Org / org-subgroup selection lives in panel 1.
      this.activePrimaryGroupKey = String(selectedId);
      this.primarySelectedKeys = { [String(selectedId)]: true };
      this.selectedTreeKeys = {};

      const primaryExpanded = { ...(this.primaryExpandedKeys || {}) };
      let currentId = selectedGroup ? Number(selectedGroup.parent_group_id || 0) : 0;
      const visited = new Set();
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        primaryExpanded[String(currentId)] = true;
        const group = this.groupsById[currentId];
        if (!group) break;
        currentId = Number(group.parent_group_id || 0);
      }
      this.primaryExpandedKeys = primaryExpanded;
    },

    async goUpLevel() {
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        let ggid =
          this.selectedPersonGlobalGroupId != null && Number(this.selectedPersonGlobalGroupId) > 0
            ? Number(this.selectedPersonGlobalGroupId)
            : this.globalGroupIdForUid(this.selectedPatientUniqueId);
        if (ggid == null || !Number(ggid)) return;
        this.selectGroup(ggid);
        return;
      }

      const g = this.selectedGroup;
      if (!g || !this.selectedGroupId) return;

      const pp = g.parent_group_id;
      const hasParent = pp != null && String(pp).trim() !== "" && Number(pp) !== 0;
      if (hasParent) {
        const pid = Number(pp);
        if (this.groupsById[pid]) {
          this.selectGroup(pid);
        }
        return;
      }

      const owner =
        g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== ""
          ? String(g.owner_unique_ID).trim()
          : "";
      if (owner) {
        const roleGid = this.globalGroupIdForUid(owner);
        // Backing out of a user-owned subgroup lands on the owner person + their member rollup.
        await this.showOwnerSubgroupRollup(owner, roleGid != null ? roleGid : undefined);
        return;
      }

      if (this.parentOfSelected) {
        this.selectGroup(Number(this.parentOfSelected.id));
      }
    },

    startRename() {
      if (!this.selectedGroupId || this.selectedGroupIsSystemDefault) return;
      this.showRenameForm = true;
      this.renameValue = this.selectedGroupName;
    },
    cancelRename() {
      this.showRenameForm = false;
      this.renameValue = "";
    },
    async saveRename() {
      if (!this.selectedGroupId || this.selectedGroupIsSystemDefault || !this.renameValue.trim()) return;
      try {
        const currentParentId =
          this.selectedGroup && this.selectedGroup.parent_group_id !== undefined
            ? this.selectedGroup.parent_group_id
            : null;
        const content = {
          Groups: {
            API_Meth: "UpdateGroup",
            accountnumber: this.accountNumber,
            group_id: this.selectedGroupId,
            name: this.renameValue.trim(),
            // Keep hierarchy unchanged during rename.
            parent_group_id: currentParentId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        if (payload && payload.status && payload.status.indexOf("Success") !== -1) {
          this.showRenameForm = false;
          this.renameValue = "";
          await this.loadGroups();
        } else {
          // still refresh to reflect changes if backend returns different message strings
          await this.loadGroups();
          this.showRenameForm = false;
          this.renameValue = "";
        }
      } catch (e) {
        console.error("Rename failed:", e);
      }
    },

    async deleteSelectedGroup() {
      if (!this.selectedGroupId || this.selectedGroupIsSystemDefault) return;
      if (!confirm(`Delete group "${this.selectedGroupName}"?`)) return;

      try {
        const content = {
          Groups: {
            API_Meth: "DeleteGroup",
            accountnumber: this.accountNumber,
            group_id: this.selectedGroupId,
            actor_unique_ID: this.actorUniqueId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        if (payload && payload.status && payload.status.indexOf("Successful") !== -1) {
          this.selectedGroupId = null;
          this.selectedPatientUniqueId = null;
          this.selectedPersonGlobalGroupId = null;
          this.activePrimaryGroupKey = null;
          this.primarySelectedKeys = {};
          this.hasSecondarySelection = false;
          this.showRenameForm = false;
          this.showCreateForm = false;
          await this.loadGroups();
        } else {
          await this.loadGroups();
        }
      } catch (e) {
        console.error("Delete failed:", e);
      }
    },

    startCreateSubgroup() {
      if (!this.selectedGroupId && !this.selectedPatientUniqueId) return;
      this.showCreateForm = true;
      this.createValue = "";
    },
    cancelCreateSubgroup() {
      this.showCreateForm = false;
      this.createValue = "";
    },
    async saveCreateSubgroup() {
      if (!this.createValue.trim()) return;
      if (!this.selectedGroupId && !this.selectedPatientUniqueId) return;

      let parent_group_id = null;
      let owner_unique_ID = null;
      if (this.selectedPatientUniqueId && !this.selectedGroupId) {
        owner_unique_ID = this.selectedPatientUniqueId;
        parent_group_id = null;
      } else if (this.selectedGroupId) {
        const g = this.selectedGroup;
        const o =
          g && g.owner_unique_ID != null && String(g.owner_unique_ID).trim() !== ""
            ? String(g.owner_unique_ID).trim()
            : null;
        if (o) {
          owner_unique_ID = o;
          parent_group_id = this.selectedGroupId;
        } else {
          parent_group_id = this.selectedGroupId;
        }
      }

      try {
        const body = {
          API_Meth: "CreateGroup",
          accountnumber: this.accountNumber,
          name: this.createValue.trim(),
          parent_group_id,
          actor_unique_ID: this.actorUniqueId,
        };
        if (owner_unique_ID) body.owner_unique_ID = owner_unique_ID;
        const content = { Groups: body };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const ok = payload && payload.status && payload.status.indexOf("Success") !== -1;
        const newGroupId = payload && payload.group_id != null ? Number(payload.group_id) : null;
        this.showCreateForm = false;
        this.createValue = "";
        await this.loadGroups();
        if (ok && newGroupId) {
          this.selectedPatientUniqueId = null;
          this.selectedPersonGlobalGroupId = null;
          this.selectedGroupId = newGroupId;
          if (owner_unique_ID) await this.fetchOwnerGroups(owner_unique_ID);
          this.syncTreeStateFromSelection();
          await this.refreshUsersPanel();
        }
      } catch (e) {
        console.error("Create subgroup failed:", e);
      }
    },

    formatMemberName(m) {
      const first = m.user_Fname || "";
      const last = m.user_Lname || "";
      return `${first} ${last}`.trim() || m.username || m.unique_ID;
    },

    userNameParts(user) {
      const firstExplicit = String(user?.first_name || user?.user_Fname || user?.firstname || "").trim();
      const lastExplicit = String(user?.last_name || user?.user_Lname || user?.lastname || "").trim();
      if (firstExplicit || lastExplicit) {
        return {
          last: lastExplicit.toLowerCase(),
          first: firstExplicit.toLowerCase(),
        };
      }

      const name = String(user?.name || user?.label || "").trim();
      if (name) {
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length === 1) {
          return { last: parts[0].toLowerCase(), first: "" };
        }
        const first = parts[0];
        const last = parts.slice(1).join(" ");
        return { last: last.toLowerCase(), first: first.toLowerCase() };
      }

      const fallback = String(user?.username || user?.email || user?.unique_ID || "").toLowerCase();
      return { last: fallback, first: "" };
    },

    compareUsersByLastFirst(a, b) {
      const aParts = this.userNameParts(a);
      const bParts = this.userNameParts(b);
      const lastCmp = aParts.last.localeCompare(bParts.last);
      if (lastCmp !== 0) return lastCmp;
      const firstCmp = aParts.first.localeCompare(bParts.first);
      if (firstCmp !== 0) return firstCmp;
      const aFallback = String(a?.name || a?.label || a?.username || a?.email || a?.unique_ID || "").toLowerCase();
      const bFallback = String(b?.name || b?.label || b?.username || b?.email || b?.unique_ID || "").toLowerCase();
      return aFallback.localeCompare(bFallback);
    },

    focusUserPanel() {
      const panel = this.$refs.usersPanel;
      if (panel && panel.scrollIntoView) {
        panel.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },

    async refreshUsersPanel() {
      // Person selected: reload the union list, not a single group's picklist.
      if (this.isOwnerSubgroupRollup) {
        await this.loadOwnerSubgroupMemberRollup();
        return;
      }
      await this.loadGroupMembers();
      if (!this.accountUsers.length) {
        await this.loadAccountUsers();
      } else {
        this.rebuildPickList();
      }
    },

    // Shared GetGroupMembers fetch used by both single-group assign and the person rollup.
    async fetchGroupMembersList(groupId) {
      const gid = Number(groupId);
      if (!this.accountNumber || !gid) return [];
      const content = {
        Groups: {
          API_Meth: "GetGroupMembers",
          accountnumber: this.accountNumber,
          group_id: gid,
        },
      };
      const res = await axios.post(CURR_API + "/tswebhook.php", content, {
        headers: { "Content-Type": "application/json;" },
      });
      const payload = this.normalizeApiPayload(res.data);
      const list = payload && payload.data ? payload.data : [];
      return Array.isArray(list) ? list : [];
    },

    // Union every member of this panel-2 user's owned subgroups (deduped by unique_ID).
    async loadOwnerSubgroupMemberRollup() {
      const owner = String(this.selectedPatientUniqueId || "").trim();
      if (!this.accountNumber || !owner) return;
      this.loadingMembers = true;
      try {
        // Names/emails for the picklist come from the account roster.
        if (!this.accountUsers.length) await this.loadAccountUsers();
        if (!Object.prototype.hasOwnProperty.call(this.loadedOwnerGroups, owner)) {
          await this.fetchOwnerGroups(owner);
        }
        const owned = this.loadedOwnerGroups[owner] || [];
        const memberships = {};
        const byId = new Map();
        // One GetGroupMembers call per owned subgroup, in parallel.
        const lists = await Promise.all(owned.map((g) => this.fetchGroupMembersList(g.id)));
        owned.forEach((g, i) => {
          const gid = Number(g.id);
          for (const m of lists[i] || []) {
            const id = String(m.unique_ID);
            if (!memberships[id]) memberships[id] = [];
            // Remember every subgroup this person is in so unassign can hit all of them.
            memberships[id].push(gid);
            if (!byId.has(id)) byId.set(id, m);
          }
        });
        this.ownerSubgroupMemberships = memberships;
        this.groupMembers = Array.from(byId.values());
        this.memberSnapshotIds = this.groupMembers.map((m) => String(m.unique_ID));
        this.rebuildPickList();
      } catch (e) {
        console.error("Failed to load owner subgroup members:", e);
        this.ownerSubgroupMemberships = {};
        this.groupMembers = [];
        this.memberSnapshotIds = [];
      } finally {
        this.loadingMembers = false;
      }
    },

    async loadGroupMembers() {
      if (!this.accountNumber || !this.selectedGroupId) return;
      this.loadingMembers = true;
      try {
        // Same endpoint as the rollup; scoped to the selected group_id only.
        this.groupMembers = await this.fetchGroupMembersList(this.selectedGroupId);
        this.memberSnapshotIds = this.groupMembers.map((m) => String(m.unique_ID));
        this.rebuildPickList();
      } catch (e) {
        console.error("Failed to load group members:", e);
        this.groupMembers = [];
        this.memberSnapshotIds = [];
      } finally {
        this.loadingMembers = false;
      }
    },

    async loadAccountUsers() {
      if (!this.accountNumber) return;
      this.loadingUsers = true;
      try {
        const content = {
          Account: {
            API_Meth: "GetAccountUsers",
            accountnumber: this.accountNumber,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const list = payload && payload.data ? payload.data : [];
        if (Array.isArray(list)) {
          this.accountUsers = list
            .map((u) => ({
              unique_ID: u.subaccountnumber || u.unique_ID || u.subAccountNumber || u.id,
              name: `${u.firstname || u.Firstname || u.user_Fname || ""} ${u.lastname || u.Lastname || u.user_Lname || ""}`.trim(),
              email: u.email || u.user_email || "",
              username: u.username || "",
              account_type: u.account_type || u.accountType || "",
              account_status: u.account_status || u.accountStatus || "",
              first_name: u.firstname || u.Firstname || u.user_Fname || "",
              last_name: u.lastname || u.Lastname || u.user_Lname || "",
            }))
            .sort((a, b) => this.compareUsersByLastFirst(a, b));
        } else {
          this.accountUsers = [];
        }
        this.rebuildPickList();
      } catch (e) {
        console.error("Failed to load account users:", e);
        this.accountUsers = [];
      } finally {
        this.loadingUsers = false;
      }
    },

    rebuildPickList() {
      const query = (this.userSearchQuery || "").trim().toLowerCase();
      const memberMap = new Map();
      this.groupMembers.forEach((m) => {
        const id = String(m.unique_ID);
        const fallbackName = this.formatMemberName(m);
        const user = this.accountUsers.find((u) => String(u.unique_ID) === id);
        memberMap.set(id, {
          unique_ID: id,
          name: user?.name || fallbackName,
          email: user?.email || "",
          username: user?.username || m.username || "",
          first_name: user?.first_name || m.user_Fname || "",
          last_name: user?.last_name || m.user_Lname || "",
        });
      });

      const inGroupUsers = Array.from(memberMap.values()).sort((a, b) => this.compareUsersByLastFirst(a, b));
      const inGroupIds = new Set(inGroupUsers.map((u) => String(u.unique_ID)));
      // Person rollup cannot assign; leave the unassigned column empty.
      let notInGroupUsers = this.isOwnerSubgroupRollup
        ? []
        : this.accountUsers
            .filter((u) => !inGroupIds.has(String(u.unique_ID)))
            .filter((u) => this.isActiveAccountUser(u))
            .filter((u) => this.matchesRoleFilter(u))
            .map((u) => ({ ...u, unique_ID: String(u.unique_ID) }))
            .sort((a, b) => this.compareUsersByLastFirst(a, b));

      if (query) {
        const matcher = (u) =>
          (u.name || "").toLowerCase().includes(query) ||
          (u.email || "").toLowerCase().includes(query) ||
          String(u.unique_ID).toLowerCase().includes(query);
        this.pickListUsers = [inGroupUsers.filter(matcher), notInGroupUsers.filter(matcher)];
      } else {
        this.pickListUsers = [inGroupUsers, notInGroupUsers];
      }
      // Clear transient listbox selections whenever list contents are rebuilt.
      this.pickListSelection = [[], []];
    },

    onPickListSelectionChange(value) {
      if (Array.isArray(value) && value.length === 2) {
        this.pickListSelection = value;
      } else {
        this.pickListSelection = [[], []];
      }
    },

    onPickListTransferClick(event) {
      const btn = event?.target?.closest?.("button[data-pc-name]");
      if (!btn) return;
      const name = String(btn.getAttribute("data-pc-name") || "").toLowerCase();
      // Source = assigned (right after CSS swap); target = unassigned (left).
      const isToTarget = name === "pcmovetotargetbutton" || name === "pcmovealltotargetbutton";
      const isToSource = name === "pcmovetosourcebutton" || name === "pcmovealltosourcebutton";
      if (!isToTarget && !isToSource) return;

      // Person rollup: block assign (visually the right arrow after the list swap).
      if (this.isOwnerSubgroupRollup && isToSource) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (this.isOwnerSubgroupRollup && isToTarget) {
        // Confirm before stripping these people from every subgroup the panel-2 user owns.
        const moving =
          name === "pcmovealltotargetbutton"
            ? this.pickListUsers[0] || []
            : (this.pickListSelection && this.pickListSelection[0]) || [];
        if (!moving.length) return;
        const names = moving.map((u) => u.name || u.unique_ID);
        const ownerName = this.patientDisplayName || "this user";
        const msg = `Are you sure you want to unassign ${names.join(", ")} from all subgroups of ${ownerName}?`;
        if (!window.confirm(msg)) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      const selectedCount = isToTarget
        ? ((this.pickListSelection && this.pickListSelection[0]) || []).length
        : ((this.pickListSelection && this.pickListSelection[1]) || []).length;

      if (selectedCount <= 1) return;

      const msg = `You are about to move ${selectedCount} accounts. Continue?`;
      if (!window.confirm(msg)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },

    async saveAssignChanges() {
      if (!this.canSaveAssignment || this.assignmentBusy) return;
      this.assignmentBusy = true;
      try {
        const original = new Set((this.memberSnapshotIds || []).map(String));
        const now = new Set((this.pickListUsers[0] || []).map((u) => String(u.unique_ID)));
        // Rollup never adds members; only RemoveUserFromGroup is allowed.
        const toAdd = this.isOwnerSubgroupRollup ? [] : Array.from(now).filter((id) => !original.has(id));
        const toRemove = Array.from(original).filter((id) => !now.has(id));

        for (const id of toAdd) {
          const content = {
            Groups: {
              API_Meth: "AddUserToGroup",
              accountnumber: this.accountNumber,
              unique_ID: id,
              group_id: this.selectedGroupId,
              actor_unique_ID: this.actorUniqueId,
            },
          };
          await axios.post(CURR_API + "/tswebhook.php", content, {
            headers: { "Content-Type": "application/json;" },
          });
        }
        for (const id of toRemove) {
          // Rollup: drop the user from every owned subgroup they were in; else one group.
          const groupIds = this.isOwnerSubgroupRollup
            ? this.ownerSubgroupMemberships[id] || []
            : [this.selectedGroupId];
          for (const gid of groupIds) {
            if (!gid) continue;
            const content = {
              Groups: {
                API_Meth: "RemoveUserFromGroup",
                accountnumber: this.accountNumber,
                unique_ID: id,
                group_id: gid,
                actor_unique_ID: this.actorUniqueId,
              },
            };
            await axios.post(CURR_API + "/tswebhook.php", content, {
              headers: { "Content-Type": "application/json;" },
            });
          }
        }

        // Refresh groups + membership caches so panel 1/2/3 stay consistent after save.
        await this.loadGroups();
      } catch (e) {
        console.error("Save assign changes failed:", e);
      } finally {
        this.assignmentBusy = false;
      }
    },

    async quitAssignChanges() {
      if (!this.canSaveAssignment || this.assignmentBusy) return;
      this.userSearchQuery = "";
      // Restore the union list instead of a single-group membership fetch.
      if (this.isOwnerSubgroupRollup) {
        await this.loadOwnerSubgroupMemberRollup();
        return;
      }
      await this.loadGroupMembers();
      this.rebuildPickList();
    },

    async addMember(uniqueId) {
      // retained for compatibility; picklist is now primary interaction
      if (!uniqueId || !this.selectedGroupId) return;
      const source = this.pickListUsers[0] || [];
      const target = this.pickListUsers[1] || [];
      const idx = target.findIndex((u) => String(u.unique_ID) === String(uniqueId));
      if (idx >= 0) {
        const [user] = target.splice(idx, 1);
        source.push(user);
        this.pickListUsers = [source, target];
      }
    },

    async removeMember(uniqueId) {
      // retained for compatibility; picklist is now primary interaction
      if (!uniqueId || !this.selectedGroupId) return;
      const source = this.pickListUsers[0] || [];
      const target = this.pickListUsers[1] || [];
      const idx = source.findIndex((u) => String(u.unique_ID) === String(uniqueId));
      if (idx >= 0) {
        const [user] = source.splice(idx, 1);
        target.push(user);
        this.pickListUsers = [source, target];
      }
    },

    getUserDisplayNameById(uniqueId) {
      const user = this.accountUsers.find((u) => String(u.unique_ID) === String(uniqueId));
      return user ? user.name : String(uniqueId);
    },

    async loadModalMembers() {
      await this.loadGroupMembers();
    },

    async loadModalUsers() {
      await this.loadAccountUsers();
    },

    async saveAssign() {
      await this.saveAssignChanges();
    },

    closeAssignModal() {
      // legacy no-op after moving users section inline
    },

    async openAssignUsers() {
      this.focusUserPanel();
      await this.refreshUsersPanel();
    },

    async addMemberToServer(uniqueId) {
      const content = {
        Groups: {
          API_Meth: "AddUserToGroup",
          accountnumber: this.accountNumber,
          unique_ID: uniqueId,
          group_id: this.selectedGroupId,
          actor_unique_ID: this.actorUniqueId,
        },
      };
      return axios.post(CURR_API + "/tswebhook.php", content, {
        headers: { "Content-Type": "application/json;" },
      });
    },

    async removeMemberFromServer(uniqueId) {
      const content = {
        Groups: {
          API_Meth: "RemoveUserFromGroup",
          accountnumber: this.accountNumber,
          unique_ID: uniqueId,
          group_id: this.selectedGroupId,
          actor_unique_ID: this.actorUniqueId,
        },
      };
      return axios.post(CURR_API + "/tswebhook.php", content, {
        headers: { "Content-Type": "application/json;" },
        });
    },
  },
};
</script>

<style scoped lang="scss">
/* Colors/fonts live in the stylebook (.eh-ds in brand-ds.scss). Layout stays here. */
.groups-page {
  padding: 1.5rem 0;
}

.groups-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0 0.25rem 1rem;
}

.groups-header-top {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.groups-header h1 {
  font-size: 1.75rem;
  margin: 0;
}

.groups-search-row {
  display: flex;
  gap: 1.25rem;
  align-items: center;
}

.search-field input {
  width: 320px;
  max-width: 80vw;
  padding: 0.55rem 0.8rem;
  border-radius: 10px;
}

.radio {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  font-size: 0.95rem;
  user-select: none;
}

.groups-body {
  margin-top: 0.5rem;
}

.groups-grid {
  display: grid;
  grid-template-columns: minmax(155px, 0.42fr) minmax(235px, 0.58fr) minmax(520px, 1.2fr);
  gap: 0.75rem;
  align-items: stretch;
}

.groups-panel {
  position: relative;
  border-radius: 14px;
  padding: 0.9rem;
  min-height: 640px;
  height: 100%;
  box-sizing: border-box;
}

.panel-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  margin-bottom: 0.55rem;
  border-radius: 50%;
  font-size: 0.82rem;
  flex: 0 0 auto;
}

.panel-help {
  min-height: 2.6em;
  margin: 0 0 0.8rem;
  font-size: 1.2rem;
  line-height: 1.3;
}

.primary-group-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.primary-group-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.7rem 0.65rem;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 0.88rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.primary-group-row .pi {
  font-size: 0.72rem;
}

.hierarchy-empty,
.assignment-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 260px;
  border: 1px dashed;
  border-radius: 12px;
  font-size: 0.88rem;
  text-align: center;
}

.panel-header-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}

.selection-prompt {
  margin-left: auto;
  text-align: right;
  font-size: 1.5rem;
}

.list-title {
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}
.mt-12 {
  margin-top: 12px;
}

.groups-tree {
  border-radius: 12px;
  padding: 0.35rem 0.25rem;
  min-height: 260px;
  max-height: 360px;
  overflow-y: auto;
}

.tree-node-label {
  font-size: 0.93rem;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  min-height: 2rem;
  padding: 0.15rem 0.35rem 0.15rem 0;
  box-sizing: border-box;
  cursor: pointer;
}

.tree-node-label-text {
  flex: 0 1 auto;
  min-width: 0;
}

.tree-node-label--group {
  font-weight: 600;
}

.tree-node-label--person {
  font-weight: 500;
  gap: 0.4rem;
}

.user-silhouette-icon {
  font-size: 0.9rem;
  line-height: 1;
}

:deep(.groups-tree .p-tree-node-content) {
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: 8px;
}

:deep(.groups-tree .p-tree-node-label),
:deep(.groups-tree .p-treenode-label),
:deep(.groups-tree .p-tree-node-content > span:not(.p-tree-node-toggle-button):last-child),
:deep(.groups-tree .p-tree-node-content > .p-tree-node-label) {
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  min-width: 0;
  width: 100%;
}

.selected-subgroups-line {
  margin-top: 0.55rem;
  font-size: 0.88rem;
}

.group-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-radius: 12px;
  max-height: 210px;
  overflow-y: auto;
}

.group-list li {
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid;
  cursor: pointer;
}

.group-list li:last-child {
  border-bottom: none;
}

.group-list li.selected {
  border-left: 4px solid;
  padding-left: 0.7rem;
  font-weight: 600;
}

.muted {
  padding: 0.7rem 0.8rem;
}

.selected-box {
  margin-top: 0.9rem;
  border-top: 1px solid;
  padding-top: 0.9rem;
}

.selected-line {
  font-size: 0.95rem;
  margin-bottom: 0.7rem;
}

.users-list-labels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
  font-size: 1.08rem;
}

.users-label-left {
  grid-column: 1;
  grid-row: 1;
}

.users-breadcrumb {
  grid-column: 1;
  grid-row: 2;
  font-size: 1.8rem;
}

.users-label-right {
  grid-column: 2;
  grid-row: 1;
  justify-self: start;
  padding-left: 2rem;
}

.selected-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.form-inline {
  margin-top: 0.9rem;
}

.form-inline input {
  width: 100%;
  padding: 0.55rem 0.8rem;
  border-radius: 10px;
}

.form-inline-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.55rem;
}

.btn {
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-weight: 600;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.small {
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
}

.users-panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.users-panel-heading > div:first-child {
  min-width: 0;
}

.assignment-column-headings {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3.4rem;
  margin: 0.3rem 0 0.6rem;
}

.assignment-column-headings > div {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.9rem;
}

.assignment-column-headings .panel-step {
  margin: 0;
}

.groups-heading,
.users-heading {
  margin-bottom: 0.6rem;
  font-size: 2.2rem;
}

.patient-users-hint {
  margin: 0 0 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  font-size: 0.92rem;
}

.patient-users-hint p {
  margin: 0;
}

.users-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}

.users-top input {
  width: 220px;
  max-width: 100%;
  padding: 0.55rem 0.8rem;
  border-radius: 10px;
}

.groups-picklist {
  margin-top: 0.65rem;
}

.pick-item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  border-radius: 8px;
  padding: 0.2rem 0.25rem;
}

.pick-name {
  font-weight: 700;
}

.pick-email {
  font-size: 0.88rem;
}

.picklist-actions {
  margin-top: 0.8rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
}

:deep(.groups-picklist .p-picklist) {
  display: flex;
  border: 1px solid;
  border-radius: 12px;
}

:deep(.groups-picklist .p-picklist-target-list-container) {
  order: 1;
  width: 100%;
}

:deep(.groups-picklist .p-picklist-transfer-controls) {
  order: 2;
}

:deep(.groups-picklist .p-picklist-source-list-container) {
  order: 3;
  width: 100%;
}

:deep(.groups-picklist .p-picklist-transfer-controls .p-icon) {
  transform: rotate(180deg);
}

/* Visually the right arrow is assign (move to source); hide it in person-rollup mode. */
.groups-picklist-wrap--no-assign :deep(button[data-pc-name="pcmovetosourcebutton"]),
.groups-picklist-wrap--no-assign :deep(button[data-pc-name="pcmovealltosourcebutton"]) {
  display: none !important;
}

:deep(.groups-picklist .p-picklist-list) {
  min-height: 300px;
}

:deep(.groups-picklist .p-picklist-header) {
  font-weight: 700;
}

:deep(.groups-picklist .p-picklist-item) {
  padding: 0.55rem 0.6rem;
}

:deep(.groups-picklist .p-picklist-buttons) {
  padding: 0 0.4rem;
}

:deep(.groups-picklist button[data-pc-name="pcmovealltotargetbutton"]),
:deep(.groups-picklist button[data-pc-name="pcmovealltosourcebutton"]) {
  display: none !important;
}

@media (max-width: 900px) {
  .groups-grid {
    grid-template-columns: 1fr;
  }
  .primary-groups-panel,
  .hierarchy-panel,
  .users-panel {
    min-height: auto;
  }
  .users-top {
    justify-content: stretch;
  }
  .users-top input {
    width: 100%;
  }
  .users-list-labels {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
}
</style>
